/**
 * Rows for the route evidence table on Settings → Nightshift → Models.
 *
 * The server returns raw counts per stage and route; this combines stages when the table shows
 * all of them (summing counts, never averaging rates) and turns counts into the figures shown.
 * A rate over nothing is null and shown as a dash, not as 0%.
 */
import type { UiModelEvidenceRow } from '@nightshift/ui-types';

export type EvidenceStage = 'all' | UiModelEvidenceRow['stage'];

export interface EvidenceLine {
  key: string;
  harness: string;
  model: string;
  thinking: string | null;
  attempts: number;
  succeeded: number;
  /** Succeeded over finished attempts. */
  successRate: number | null;
  reviewed: number;
  /** First-review passes over implementations that reached a review. */
  firstPassRate: number | null;
  /** Mean priced cost of a successful attempt. */
  avgSuccessUsd: number | null;
  spendUsd: number;
}

function rate(part: number, whole: number): number | null {
  return whole > 0 ? part / whole : null;
}

export function evidenceLines(rows: readonly UiModelEvidenceRow[], stage: EvidenceStage): EvidenceLine[] {
  const merged = new Map<string, UiModelEvidenceRow>();
  for (const row of rows) {
    if (stage !== 'all' && row.stage !== stage) continue;
    const key = [row.harness, row.model, row.thinking ?? ''].join('\u0000');
    const prior = merged.get(key);
    if (!prior) {
      merged.set(key, { ...row });
      continue;
    }
    prior.attempts += row.attempts;
    prior.succeeded += row.succeeded;
    prior.reviewed += row.reviewed;
    prior.firstPass += row.firstPass;
    prior.successUsd += row.successUsd;
    prior.successPriced += row.successPriced;
    prior.spendUsd += row.spendUsd;
  }
  return [...merged.entries()]
    .map(([key, row]) => ({
      key,
      harness: row.harness,
      model: row.model,
      thinking: row.thinking,
      attempts: row.attempts,
      succeeded: row.succeeded,
      successRate: rate(row.succeeded, row.attempts),
      reviewed: row.reviewed,
      firstPassRate: rate(row.firstPass, row.reviewed),
      avgSuccessUsd: row.successPriced > 0 ? row.successUsd / row.successPriced : null,
      spendUsd: row.spendUsd,
    }))
    .sort((a, b) => b.spendUsd - a.spendUsd || b.attempts - a.attempts || a.model.localeCompare(b.model));
}

/** `83%`, or a dash when there is nothing to divide by. */
export function percent(value: number | null): string {
  return value === null ? '—' : `${Math.round(value * 100)}%`;
}
